import { inject, Injectable, Injector } from '@angular/core';
import { ViewMode } from './types';
import {
  combineLatest,
  firstValueFrom,
  Observable,
  ReplaySubject,
  distinctUntilChanged,
  filter,
  map,
  shareReplay,
  startWith,
  tap,
} from 'rxjs';
import { NavigationEnd, Router, UrlTree } from '@angular/router';
import { ProductAnalyticsService } from '@vendasta/product-analytics';
import { ConversationService } from './conversation.service';
import { toFirestoreId } from './conversation-utils';

const localStorageId = 'conversation-view-mode';
const AI_ROUTE = 'ai';
const INBOX_ROUTE = 'inbox';

@Injectable({ providedIn: 'root' })
export class ViewModeService {
  private readonly router = inject(Router);
  private readonly analyticsService = inject(ProductAnalyticsService);
  private readonly injector = inject(Injector);

  private readonly viewMode$$ = new ReplaySubject<ViewMode>(1);
  public readonly viewMode$ = this.viewMode$$.pipe(
    startWith(this.getUserViewMode || 'sidebar'),
    distinctUntilChanged(),
    tap((mode) => localStorage.setItem(localStorageId, mode)),
    tap((mode) =>
      this.analyticsService.trackEvent(INBOX_ROUTE, 'inbox-view-mode', 'state', 0, {
        viewMode: mode,
      }),
    ),
    shareReplay(1),
  );

  public readonly isOpen$ = this.router.events.pipe(
    filter((event) => event instanceof NavigationEnd),
    map((event: NavigationEnd) => this.router.parseUrl(event.url)),
    map((urlTree) => this.checkSegment(urlTree, 0, INBOX_ROUTE)),
    startWith(window.location.pathname.includes('(inbox:inbox')),
    distinctUntilChanged(),
    shareReplay(1),
  );

  public readonly isAIOpen$ = this.router.events.pipe(
    filter((event) => event instanceof NavigationEnd),
    map((event: NavigationEnd) => this.router.parseUrl(event.url)),
    map((urlTree) => this.checkSegment(urlTree, 1, AI_ROUTE)),
    startWith(window.location.pathname.includes('(inbox:inbox/ai')),
    distinctUntilChanged(),
    shareReplay(1),
  );

  public readonly isInboxOpen$: Observable<boolean> = combineLatest([this.isOpen$, this.isAIOpen$]).pipe(
    map(([isOpen, isAIOpen]) => isOpen && !isAIOpen),
    distinctUntilChanged(),
    shareReplay(1),
  );

  public readonly isDrawerOpen$ = combineLatest([this.viewMode$, this.isOpen$]).pipe(
    map(([mode, isOpen]) => mode === 'sidebar' && isOpen),
  );

  public readonly isModalOpen$ = combineLatest([this.viewMode$, this.isOpen$]).pipe(
    map(([mode, isOpen]) => mode === 'modal' && isOpen),
  );

  public async toggleViewMode(): Promise<void> {
    const currentMode = await firstValueFrom(this.viewMode$);
    this.viewMode$$.next(currentMode === 'modal' ? 'sidebar' : 'modal');
  }

  private conversationService?: ConversationService;

  public async toggleInboxOpen(): Promise<void> {
    if (this.getCurrentRoute(0) === INBOX_ROUTE && this.getCurrentRoute(1) !== AI_ROUTE) {
      this.close();
    } else {
      if (!this.conversationService) {
        this.conversationService = this.injector.get(ConversationService);
      }
      const conversationId = await firstValueFrom(this.conversationService.currentFirestoreConversationId$);
      this.open(conversationId);
    }
  }

  public async open(conversationId?: string, prefilledMessage?: string): Promise<void> {
    const viewMode = await firstValueFrom(this.viewMode$);
    this.router.navigate(this.routerCommands(viewMode, conversationId), {
      queryParamsHandling: 'merge',
      queryParams: { inboxPrefilledMessage: prefilledMessage || null },
    });
  }

  public openSettings(): void {
    this.router
      .navigate(['', { outlets: { inbox: 'inbox/settings' } }], {
        queryParamsHandling: 'merge',
        queryParams: { inboxPrefilledMessage: null },
      })
      .then(() => this.analyticsService.trackEvent('inbox', 'platform-settings', 'open'));
  }

  // the type any[] is the type used by router.navigate
  public routerCommands(mode: ViewMode, conversationId?: string): unknown[] {
    const path = conversationId ? `inbox/conversations/${toFirestoreId(conversationId)}` : INBOX_ROUTE;
    return [
      '',
      {
        outlets: {
          inbox: path,
        },
      },
    ];
  }

  close(): void {
    this.router.navigate(['', { outlets: { inbox: null } }], {
      queryParamsHandling: 'merge',
      queryParams: { inboxPrefilledMessage: null },
    });
  }

  private getCurrentRoute(index: number): string {
    const currentUrlTree = this.router.parseUrl(this.router.url);
    return currentUrlTree.root.children?.inbox?.segments[index]?.path;
  }

  private get getUserViewMode(): ViewMode {
    return localStorage.getItem(localStorageId) as ViewMode;
  }

  public toggleInboxAIOpen(): void {
    const currentUrlTree = this.router.parseUrl(this.router.url);
    const path = currentUrlTree.root.children?.inbox?.segments[1]?.path;
    if (path === AI_ROUTE) {
      this.closeInboxAI();
    } else {
      this.viewMode$$.next('sidebar');
      this.openInboxAI();
    }
  }
  public async openInboxAI(): Promise<void> {
    this.router.navigate([{ outlets: { inbox: 'inbox/ai' } }]);
  }
  public async closeInboxAI(): Promise<void> {
    this.router.navigate([{ outlets: { inbox: null } }], {
      queryParamsHandling: 'merge',
    });
  }

  private checkSegment(urlTree: UrlTree, segmentIndex: number, segmentPath: string): boolean {
    return urlTree.root.children?.inbox?.segments[segmentIndex]?.path === segmentPath;
  }
}
